/**
 * Development Logger Utility
 * 
 * Provides structured logging for development environment with different log levels.
 * Logs are only shown in development mode to keep production clean.
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error'

interface LogContext {
  component?: string
  action?: string
  data?: any
  error?: Error | string | unknown
  timestamp?: string
  // Conversation and messaging
  conversationId?: string
  messageId?: string
  messageLength?: number
  messageCount?: number
  messageContent?: string
  // Chatbot related
  chatbotId?: string
  chatbotName?: string
  // User and authentication
  userId?: string
  userEmail?: string
  userName?: string
  userType?: string
  agentId?: string
  agentName?: string
  // WebSocket and real-time
  type?: string
  status?: string
  connected?: boolean
  baseUrl?: string
  // Analytics and metrics
  totalNotes?: number
  totalMarked?: number
  responseTime?: number
  // Form and UI
  currentStep?: string | number
  query?: string
  filter?: string
  key?: string
  clearedKeys?: string[]
  // Priority and workflow
  priority?: string
  oldPriority?: string
  newPriority?: string
  // API and network
  method?: string
  url?: string
  statusCode?: number
  duration?: number
  // Store and state management
  storeKey?: string
  // General purpose
  [key: string]: any
}

class Logger {
  private isDevelopment: boolean
  private enableLogging: boolean

  constructor() {
    this.isDevelopment = import.meta.env.VITE_NODE_ENV === 'development'
    this.enableLogging = import.meta.env.VITE_DEBUG === 'true' || this.isDevelopment
  }

  /**
   * Debug level logging - for detailed development information
   */
  debug(message: string, context?: LogContext): void {
    if (!this.enableLogging) return
    
    this.log('debug', message, context)
  }

  /**
   * Info level logging - for general information
   */
  info(message: string, context?: LogContext): void {
    if (!this.enableLogging) return
    
    this.log('info', message, context)
  }

  /**
   * Warning level logging - for potential issues
   */
  warn(message: string, context?: LogContext): void {
    if (!this.enableLogging) return
    
    this.log('warn', message, context)
  }

  /**
   * Error level logging - for errors and failures
   */
  error(message: string, context?: LogContext): void {
    if (!this.enableLogging) return
    
    this.log('error', message, context)
  }

  /**
   * API Request logging - for tracking API calls
   */
  apiRequest(method: string, url: string, data?: any): void {
    if (!this.enableLogging) return
    
    this.info(`🔗 API Request: ${method.toUpperCase()} ${url}`, {
      component: 'API',
      action: 'request',
      data: data ? { payload: data } : undefined
    })
  }

  /**
   * API Response logging - for tracking API responses
   */
  apiResponse(method: string, url: string, status: number, data?: any): void {
    if (!this.enableLogging) return
    
    const emoji = status >= 200 && status < 300 ? '✅' : '❌'
    this.info(`${emoji} API Response: ${method.toUpperCase()} ${url} (${status})`, {
      component: 'API',
      action: 'response',
      data: { status, response: data }
    })
  }

  /**
   * Component lifecycle logging
   */
  component(componentName: string, action: string, data?: any): void {
    if (!this.enableLogging) return
    
    this.debug(`🔄 Component: ${componentName} - ${action}`, {
      component: componentName,
      action,
      data
    })
  }

  /**
   * Authentication flow logging
   */
  auth(action: string, data?: any): void {
    if (!this.enableLogging) return
    
    this.info(`🔐 Auth: ${action}`, {
      component: 'Auth',
      action,
      data
    })
  }

  /**
   * Store/State logging
   */
  store(storeName: string, action: string, data?: any): void {
    if (!this.enableLogging) return
    
    this.debug(`🗃️ Store: ${storeName} - ${action}`, {
      component: storeName,
      action,
      data
    })
  }

  /**
   * Performance timing logging
   */
  performance(label: string, duration: number): void {
    if (!this.enableLogging) return
    
    this.info(`⚡ Performance: ${label} took ${duration}ms`, {
      component: 'Performance',
      action: 'timing',
      data: { label, duration }
    })
  }

  /**
   * Internal logging method
   */
  private log(level: LogLevel, message: string, context?: LogContext): void {
    const timestamp = new Date().toISOString()
    const logContext = { ...context, timestamp }
    
    const logFunction = console[level] || console.log
    
    // Enhanced formatting for development
    const prefix = this.getLogPrefix(level)
    
    if (context && Object.keys(context).length > 0) {
      logFunction(`${prefix} ${message}`, logContext)
    } else {
      logFunction(`${prefix} ${message}`)
    }
  }

  /**
   * Get styled prefix for log level
   */
  private getLogPrefix(level: LogLevel): string {
    const prefixes = {
      debug: '🐛 [DEBUG]',
      info: 'ℹ️ [INFO]',
      warn: '⚠️ [WARN]',
      error: '❌ [ERROR]'
    }
    
    return prefixes[level] || '[LOG]'
  }
}

// Export singleton logger instance
export const logger = new Logger()

// Export logger class for testing
export { Logger }

// Convenience functions for common logging patterns
export const loggerUtils = {
  /**
   * Log function entry with parameters
   */
  functionEntry: (functionName: string, params?: any) => {
    logger.debug(`→ Entering ${functionName}`, {
      action: 'function_entry',
      data: params
    })
  },

  /**
   * Log function exit with return value
   */
  functionExit: (functionName: string, returnValue?: any) => {
    logger.debug(`← Exiting ${functionName}`, {
      action: 'function_exit',
      data: returnValue
    })
  },

  /**
   * Log form validation errors
   */
  formValidation: (formName: string, errors: any) => {
    logger.warn(`📝 Form validation failed: ${formName}`, {
      component: 'Form',
      action: 'validation_error',
      data: errors
    })
  },

  /**
   * Log user interactions
   */
  userAction: (action: string, element?: string, data?: any) => {
    logger.info(`👤 User Action: ${action}${element ? ` on ${element}` : ''}`, {
      component: 'UserInteraction',
      action,
      data
    })
  }
}